/**
 * API Service for Quantros
 */
import axios from 'axios';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';
import { createInflightRequests } from '../utils/inflightRequests';
import { createPrefetchCache } from '../utils/prefetchCache';

export const API_BASE_URL = resolveApiBaseUrl(window.location, process.env.REACT_APP_API_BASE_URL);

// Ordinary requests fail promptly; expensive operations opt into longer deadlines.
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const concurrentRead = createInflightRequests();
const readIdentity = () => JSON.stringify([sessionStorage.getItem('access_token'), sessionStorage.getItem('company_slug')]);
const prefetched = createPrefetchCache(readIdentity, { ttl: 120000, maxEntries: 128 });
const domainReads = createPrefetchCache(readIdentity, { ttl: 60000, maxEntries: 16 });
const panelReads = createPrefetchCache(readIdentity, { ttl: 60000, maxEntries: 128 });
const reusablePanel = path => path === '/company/audit' || path === '/company/usage' || /^\/domains\/[^/]+\/members$/.test(path) || path.startsWith('/reports/sender/') || /^\/agent-connections\/[^/]+\/[^/]+\/history$/.test(path);
let readGeneration = 0;
const readKey = (path, config) => JSON.stringify([readIdentity(), readGeneration, path, config]);
function read(path, config = {}) {
  const key = readKey(path, config);
  if (reusablePanel(path)) return panelReads.prefetch(key, () => concurrentRead(key, () => api.get(path, config)));
  return prefetched.read(key, () => concurrentRead(key, () => api.get(path, config)));
}
export function invalidatePrefetchedData() { readGeneration += 1; prefetched.invalidate(); domainReads.invalidate(); panelReads.invalidate(); }
export function prefetchRead(path, config = {}) {
  const identity = readIdentity();
  const key = readKey(path, config);
  return (path === '/domains' ? domainReads : reusablePanel(path) ? panelReads : prefetched).prefetch(key, () => {
    if (identity !== readIdentity() || !sessionStorage.getItem('access_token')) return Promise.reject(new Error('Workspace changed'));
    return concurrentRead(key, () => api.get(path, { ...config, speculative: true }));
  });
}

// Tenant credentials stay in this tab and origin. Never send a company header in production.
export function companyHeaders() {
  const slug = sessionStorage.getItem('company_slug');
  return ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname) && slug
    ? { 'X-Company-Slug': slug } : {};
}

// Add token to requests if available
api.interceptors.request.use((config) => {
  if (config.method && config.method.toLowerCase() !== 'get') invalidatePrefetchedData();
  Object.assign(config.headers, companyHeaders());
  const token = sessionStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors
api.interceptors.response.use(
  (response) => {
    if (response.config?.method && response.config.method.toLowerCase() !== 'get') invalidatePrefetchedData();
    return response;
  },
  (error) => {
    if ([401,403].includes(error.response?.status)) invalidatePrefetchedData();
    if (!error.config?.speculative && error.response?.status === 401 && !['/auth/login', '/company/access'].includes(error.config?.url)) {
      // Unauthorized - redirect to login
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.access_token) {
      sessionStorage.setItem('access_token', response.data.access_token);
      sessionStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');
  },

  getMe: async () => {
    const token = sessionStorage.getItem('access_token');
    const response = await read('/auth/me');
    if (sessionStorage.getItem('access_token') === token) sessionStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  },

  changePassword: async (payload) => {
    const response = await api.post('/auth/change-password', payload);
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/auth/users');
    return response.data;
  },

  getRoles: async () => {
    const response = await api.get('/auth/roles');
    return response.data;
  },

  getTeams: async () => {
    const response = await api.get('/auth/teams');
    return response.data;
  },

  createRole: async (payload) => {
    const response = await api.post('/auth/roles', payload);
    return response.data;
  },

  createUser: async (payload) => {
    const response = await api.post('/auth/users', payload);
    return response.data;
  },

  updateUser: async (userId, payload) => {
    const response = await api.put(`/auth/users/${userId}`, payload);
    return response.data;
  },

  resetUserPassword: async (userId, payload) => {
    const response = await api.post(`/auth/users/${userId}/reset-password`, payload);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  },

  getCurrentUser: () => {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!sessionStorage.getItem('access_token');
  },
};

// Sessions API
export const sessionsAPI = {
  getSessions: async () => {
    const response = await read('/sessions');
    return response.data;
  },

  createSession: async (title = 'New Chat', domain, options = {}) => {
    const response = await api.post('/sessions', { title, domain }, options);
    return response.data;
  },

  getMessages: async (sessionId) => {
    const response = await read(`/sessions/${sessionId}/messages`);
    return response.data;
  },

  getAgentRuns: async (sessionId, params = {}) => {
    const response = await read(`/sessions/${sessionId}/runs`, { params: { limit: 20, ...params } });
    return response.data;
  },

  stopRun: async (sessionId, runId) => (await api.post(`/sessions/${sessionId}/runs/${runId}/stop`)).data,
  answerRun: async (sessionId, runId, answer) => (await api.post(`/sessions/${sessionId}/runs/${runId}/answer`, answer)).data,

  downloadSqlResultCsv: async (sessionId, runId, queryEventId) => {
    const response = await api.get(
      `/sessions/${sessionId}/runs/${runId}/events/${queryEventId}/csv`,
      { responseType: 'blob', timeout: 600000 }
    );
    return response.data;
  },

  sendMessage: async (sessionId, content) => {
    // Extended timeout for agent processing (5 minutes)
    const response = await api.post(`/sessions/${sessionId}/messages`, { content }, {timeout: 600000});
    return response.data;
  },

  submitMessage: async (sessionId, content, options = {}) => {
    const response = await api.post(`/sessions/${sessionId}/messages/submit`, { content }, options);
    return response.data;
  },

  deleteSession: async (sessionId) => {
    const response = await api.delete(`/sessions/${sessionId}`);
    return response.data;
  },
};


export const knowledgeAPI = {
  cached: (domain, csvEnabled = true) => {
    const config = { params: { domain } };
    const entries = prefetched.peek(readKey('/knowledge', config))?.data;
    const sources = prefetched.peek(readKey('/data-sources', config))?.data;
    const uploads = csvEnabled ? prefetched.peek(readKey('/knowledge/uploads', config))?.data : [];
    return entries && sources && uploads ? { entries, sources, uploads, time: Date.now() } : undefined;
  },
  importTemplate: async (kind) => (await api.get(`/knowledge/import-template/${kind}`, { responseType: 'blob' })).data,
  importKnowledge: async (payload) => (await api.post('/knowledge/import-csv', payload, { headers: { 'Content-Type': 'multipart/form-data' } })).data,
  list: async (domain) => (await read('/knowledge', { params: { domain } })).data,
  create: async (entry) => (await api.post('/knowledge', entry)).data,
  update: async (id, entry) => (await api.put(`/knowledge/${id}`, entry)).data,
  retrievalPlayground: async (payload) => (await api.post('/knowledge/retrieval-playground', payload, { timeout: 180000 })).data,
  generateColumnDescriptions: async (payload) => (await api.post('/knowledge/generate-column-descriptions', payload, { timeout: 180000 })).data,
  approve: async (id) => (await api.post(`/knowledge/${id}/approve`)).data,
  requestApproval: async (id) => (await api.post(`/knowledge/${id}/request-approval`)).data,
  delete: async (id) => (await api.delete(`/knowledge/${id}`)).data,
  events: async (id) => (await api.get(`/knowledge/${id}/events`)).data,
  csvUploads: async (domain) => (await read('/knowledge/uploads', { params: { domain } })).data,
  uploadCsv: async (payload) => (await api.post('/knowledge/uploads', payload, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })).data,
  replaceCsv: async (id, payload) => (await api.post(`/knowledge/uploads/${id}/replace`, payload, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })).data,
  addCsvToSchema: async (id) => (await api.post(`/knowledge/uploads/${id}/add-to-schema`)).data,
  dropCsv: async (id) => (await api.delete(`/knowledge/uploads/${id}`)).data,
};

export const domainsAPI = {
  cached: (params = {}) => domainReads.peek(readKey('/domains', { params }))?.data,
  list: async (params = {}, { force = false } = {}) => {
    const config = { params };
    const key = readKey('/domains', config);
    if (force) domainReads.remove(key);
    return (await domainReads.prefetch(key, () => concurrentRead(key, () => api.get('/domains', config)))).data;
  },
  create: async (payload) => (await api.post('/domains', payload)).data,
  update: async (id, payload) => (await api.put(`/domains/${id}`, payload)).data,
  archive: async (id) => (await api.post(`/domains/${id}/archive`)).data,
  restore: async (id) => (await api.post(`/domains/${id}/restore`)).data,
  remove: async (id) => (await api.delete(`/domains/${id}`, { timeout: 180000 })).data,
  members: async (id) => (await read(`/domains/${id}/members`)).data,
  saveMembers: async (id, user_ids) => (await api.put(`/domains/${id}/members`, { user_ids })).data,
};

export const agentConnectionsAPI = {
  cached: domain => {
    const config = { params: { domain } };
    const data = prefetched.peek(readKey('/agent-connections', config))?.data;
    const connections = prefetched.peek(readKey('/model-connections', config))?.data;
    return data && connections ? { ...data, connections } : undefined;
  },
  reindex: async (domain, agentKey, afterId) => (await api.post(`/agent-connections/${domain}/${agentKey}/reindex`, null, { params: { after_id: afterId }, timeout: 180000 })).data,
  connections: async (domain) => (await read('/model-connections', { params: { domain } })).data,
  createConnection: async (payload) => (await api.post('/model-connections', payload)).data,
  updateConnection: async (id, payload) => (await api.put(`/model-connections/${id}`, payload)).data,
  removeConnection: async (id) => (await api.delete(`/model-connections/${id}`)).data,
  testConnection: async (id) => (await api.post(`/model-connections/${id}/test`, {}, { timeout: 30000 })).data,
  get: async (domain) => (await read('/agent-connections', { params: { domain } })).data,
  update: async (domain, agentKey, payload) => (await api.put(`/agent-connections/${domain}/${agentKey}`, payload)).data,
  history: async (domain, agentKey) => (await read(`/agent-connections/${domain}/${agentKey}/history`)).data,
  chatgptAccount: async () => (await read('/chatgpt/account')).data,
  chatgptModels: async () => (await api.get('/chatgpt/models')).data,
  connectChatGPT: async () => (await api.post('/chatgpt/connect')).data,
  disconnectChatGPT: async () => (await api.delete('/chatgpt/account')).data,
};

export const dataSourcesAPI = {
  cached: domain => prefetched.peek(readKey('/data-sources', { params: { domain } }))?.data,
  list: async (domain) => (await read('/data-sources', { params: { domain } })).data,
  test: async (source) => (await api.post('/data-sources/test', source, { timeout: 30000 })).data,
  testSaved: async (id) => (await api.post(`/data-sources/${id}/test`, {}, { timeout: 30000 })).data,
  create: async (source) => (await api.post('/data-sources', source)).data,
  remove: async (id) => (await api.delete(`/data-sources/${id}`)).data,
  updateUploadSchema: async (id, schema_name) => (await api.put(`/data-sources/${id}/upload-schema`, { schema_name })).data,
  tables: async (id, schemaName) => (await api.get(`/data-sources/${id}/tables`, { params: { schema_name: schemaName }, timeout: 30000 })).data,
  columns: async (id, table) => (await api.get(`/data-sources/${id}/columns`, { params: { table }, timeout: 30000 })).data,
};

export default api;


export const companyAPI = {
  context: async () => (await read('/company/context')).data,
  consume: async ticket => (await api.post('/company/access', { ticket })).data,
  switcher: async () => (await api.get('/company/switcher')).data,
  switch: async id => (await api.post(`/company/switch/${id}`)).data,
};

const platform = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });
platform.interceptors.request.use(config => {
  const token = sessionStorage.getItem('platform_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
export const feedbackAPI = {
  get: async (kind, id) => (await api.get(`/feedback/${kind}/${encodeURIComponent(id)}`)).data,
  save: async (kind, id, rating) => (await api.put(`/feedback/${kind}/${encodeURIComponent(id)}`, {rating})).data,
  clear: async (kind, id) => (await api.delete(`/feedback/${kind}/${encodeURIComponent(id)}`)).data,
};

export const platformAPI = {
  feedback: async params => (await platform.get('/platform/feedback', {params})).data,
  me: async () => (await platform.get('/platform/me')).data,
  staff: async () => (await platform.get('/platform/staff')).data,
  createStaff: async payload => (await platform.post('/platform/staff', payload)).data,
  updateStaff: async (id, payload) => (await platform.put(`/platform/staff/${id}`, payload)).data,
  logout: async () => { await platform.post('/platform/logout'); sessionStorage.removeItem('platform_token'); },
  login: async payload => {
    const data = (await platform.post('/platform/login', payload)).data;
    sessionStorage.setItem('platform_token', data.access_token);
    return data;
  },
  list: async () => (await platform.get('/platform/companies')).data,
  create: async payload => (await platform.post('/platform/companies', payload)).data,
  status: async (id, is_active, version) => (await platform.patch(`/platform/companies/${id}`, { is_active, version })).data,
  enter: async id => (await platform.post(`/platform/companies/${id}/enter`)).data,
};

export const reportsAPI = {
  shareCandidates: (id, q) => read(`/reports/${id}/share-candidates`, { params: { q } }).then(r => r.data),
  shares: id => read(`/reports/${id}/shares`).then(r => r.data),
  share: (id, email) => api.post(`/reports/${id}/shares`, { email }).then(r => r.data),
  unshare: (id, userId) => api.delete(`/reports/${id}/shares/${userId}`).then(r => r.data),
  cachedDetail: id => prefetched.peek(readKey(`/reports/${id}`, {}))?.data,
  list: (params = {}) => read('/reports', { params }).then(r => r.data),
  detail: id => read(`/reports/${id}`).then(r => r.data),
  capabilities: () => read('/reports/capabilities').then(r => r.data.permissions),
  chat: id => read(`/reports/chat/${id}`).then(r => r.data),
  revise: id => api.post(`/reports/${id}/revise`).then(r => r.data),
  confirm: (id, version) => api.post(`/reports/${id}/confirm`, { version }).then(r => r.data),
  refresh: id => api.post(`/reports/${id}/refresh`).then(r => r.data),
  settings: (id, body) => api.put(`/reports/${id}/settings`, body).then(r => r.data),
  remove: id => api.delete(`/reports/${id}`).then(r => r.data),
  test: id => api.post(`/reports/${id}/test-email`).then(r => r.data),
  retry: (id, delivery) => api.post(`/reports/${id}/deliveries/${delivery}/retry`).then(r => r.data),
  sender: domain => read(`/reports/sender/${domain}`).then(r => r.data),
  saveSender: (domain, email, api_key, provider = 'hostinger') => api.put(`/reports/sender/${domain}`, { email, provider, api_key: api_key || null }).then(r => r.data),
  preview: (id, run) => read(`/reports/${id}/snapshots/${run}/preview`).then(r => r.data),
  pdf: (id, run) => api.get(`/reports/${id}/snapshots/${run}/pdf`, { responseType: 'blob' }).then(r => r.data),
};

export const evaluationsAPI = {
  register: (runId, domainId, sessionId, agentRunId) => api.post(`/evaluations/${runId}/executions`, { session_id: sessionId, agent_run_id: agentRunId }, { params: { domain_id: domainId } }).then(r => r.data),
  stop: (runId, domainId) => api.post(`/evaluations/${runId}/stop`, null, { params: { domain_id: domainId } }).then(r => r.data),
  status: (runId, domainId) => api.get(`/evaluations/${runId}/status`, { params: { domain_id: domainId } }).then(r => r.data),
  list: domainId => api.get('/evaluations', { params: { domain_id: domainId } }).then(r => r.data),
  save: payload => api.post('/evaluations', payload, { timeout: 120000 }).then(r => r.data),
  download: (runId, domainId) => api.get('/evaluations/' + encodeURIComponent(runId) + '/csv', { params: { domain_id: domainId }, responseType: 'blob' }).then(r => r.data),
};

export const companySettingsAPI = (companyId = null) => {
  const client = companyId ? platform : api;
  const base = companyId ? `/platform/companies/${companyId}` : '/company';
  return {
    get: () => (companyId ? client.get(`${base}/settings`) : read(`${base}/settings`)).then(r => r.data),
    usage: (params, { force = false } = {}) => {
      if (force && !companyId) panelReads.remove(readKey(`${base}/usage`, { params }));
      return (companyId ? client.get(`${base}/usage`, { params }) : read(`${base}/usage`, { params })).then(r => r.data);
    },
    audit: () => (companyId ? client.get(`${base}/audit`) : read(`${base}/audit`)).then(r => r.data),
    features: body => client.patch(`${base}/features`, body).then(r => r.data),
    capacity: () => client.get(`${base}/capacity`).then(r => r.data),
    saveCapacity: body => client.put(`${base}/capacity`, body).then(r => r.data),
    schedule: body => client.put(`${base}/pause-schedule`, body).then(r => r.data),
    saveRole: role => (role.is_system ? client.put(`${base}/roles/builtin/${encodeURIComponent(role.name)}`, role) : role.id ? client.put(`${base}/roles/${role.id}`, role) : client.post(`${base}/roles`, role)).then(r => r.data),
    deleteRole: id => client.delete(`${base}/roles/${id}`),
    userDomains: () => companyId ? client.get(`${base}/domains`).then(r => r.data) : domainsAPI.list(),
    createUser: body => companyId ? client.post(`${base}/users`, body).then(r => r.data) : authAPI.createUser(body),
    assign: (id, role) => client.put(`${base}/users/${id}/role`, { role }),
  };
};
export const getCompanyCapabilities = () => api.get('/company/capabilities').then(r => r.data);

// Widget execution deliberately bypasses speculative/cached reads.
export const widgetsAPI = {
  getView: (id, runId) => api.get(`/widgets/${id}/view`, {params:runId ? {run_id:runId} : {}}).then(r => r.data),
  salesOwners: async (id) => (await api.get(`/widgets/${id}/sales-owners`)).data,
  get: (id) => api.get(`/widgets/${id}`).then(r => r.data),
  getRun: (id, runId) => api.get(`/widgets/${id}/runs/${runId}`).then(r => r.data),
  run: (id, body) => api.post(`/widgets/${id}/runs`, body, { timeout: 240000 }).then(r => r.data),
  save: (id, body) => api.put(`/widgets/${id}`, body).then(r => r.data),
};

export const dashboardsAPI = {
  shareCandidates: (id, q) => read(`/dashboards/${id}/share-candidates`, { params: { q } }).then(r => r.data),
  shares: id => read(`/dashboards/${id}/shares`).then(r => r.data),
  share: (id, email) => api.post(`/dashboards/${id}/shares`, { email }).then(r => r.data),
  unshare: (id, userId) => api.delete(`/dashboards/${id}/shares/${userId}`).then(r => r.data),
  move: async (id, itemId, body) => (await api.post(`/dashboards/${id}/widgets/${itemId}/move`, body)).data,
  layout: (id, revision, layouts) => api.put(`/dashboards/${id}/layout`, { revision, layouts }).then(r => r.data),
  display: (id, itemId, revision, display_mode) => api.put(`/dashboards/${id}/widgets/${itemId}/display`, { revision, display_mode }).then(r => r.data),
  list: () => read('/dashboards').then(r => r.data),
  create: body => api.post('/dashboards', body).then(r => r.data),
  get: id => read(`/dashboards/${id}`).then(r => r.data),
  prefetch: id => prefetchRead(`/dashboards/${id}`),
  attach: (id, body) => api.post(`/dashboards/${id}/widgets`,body).then(r => r.data),
  refresh: (id, item, revision, inputs = {}) => api.post(`/dashboards/${id}/widgets/${item}/refresh`,{revision,...inputs},{timeout:240000}).then(r => r.data),
  remove: (id, item, revision) => api.delete(`/dashboards/${id}/widgets/${item}`,{params:{revision}}).then(r => r.data),
};
